import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { ICoursesContract } from "../model";
import { IEnrollmentsContract } from "@features/enrollments/model";
import { createEvent, createStore, sample } from "effector";
import { ICourse } from "@features/shared/entities/courses";
import { UserEnrollmentStatus } from "@features/shared/entities/status";
import { IRouterContract } from "@features/shared/router";

const challengeCardClicked = createEvent<ICourse>();

const $challenges = createStore<ICourse[]>([]);
const $newChallenges = createStore<ICourse[]>([]);
const $challengesInProgress = createStore<ICourse[]>([]);
const $finishedChallenges = createStore<ICourse[]>([]);

const contract = new ModelContract(
    {
        $challenges,
        $newChallenges,
        $challengesInProgress,
        $finishedChallenges
    },
    { challengeCardClicked },
    {},
    {}
);

export type IChallengesContract = typeof contract;

export class ChallengesModule extends ModuleDescription<IChallengesContract> {
    constructor(
        public coursesModule: ICoursesContract,
        public enrollmentsModule: IEnrollmentsContract,
        public routerModule: IRouterContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            source: this.coursesModule.stores.$courses,
            fn: (courses) =>
                courses.filter((course) => course.type === "challenge" || course.tags?.includes("challenge")),
            target: $challenges
        });

        sample({
            source: { challenges: $challenges, enrollmentsCourses: this.enrollmentsModule.stores.$enrollmentsCourses },
            fn: (s) => {
                const enrollmentsByCourseId = new Map(
                    s.enrollmentsCourses.map((enrollment) => [enrollment.course_id, enrollment])
                );

                return s.challenges.filter((course) => {
                    const enrollment = enrollmentsByCourseId.get(course.id);
                    return !enrollment;
                });
            },
            target: $newChallenges
        });

        sample({
            source: { challenges: $challenges, enrollmentsCourses: this.enrollmentsModule.stores.$enrollmentsCourses },
            fn: (s) => {
                const enrollmentsByCourseId = new Map(
                    s.enrollmentsCourses.map((enrollment) => [enrollment.course_id, enrollment])
                );

                return s.challenges.filter((course) => {
                    const enrollment = enrollmentsByCourseId.get(course.id);
                    if (!enrollment) return false;

                    const numericProgress = Number.isFinite(enrollment.progress) ? (enrollment.progress as number) : 0;
                    const isFinished = enrollment.status === UserEnrollmentStatus.Completed || numericProgress >= 100;

                    return enrollment.status === UserEnrollmentStatus.Progress && !isFinished;
                });
            },
            target: $challengesInProgress
        });

        sample({
            source: { challenges: $challenges, enrollmentsCourses: this.enrollmentsModule.stores.$enrollmentsCourses },
            fn: (s) => {
                const enrollmentsByCourseId = new Map(
                    s.enrollmentsCourses.map((enrollment) => [enrollment.course_id, enrollment])
                );

                return s.challenges.filter((course) => {
                    const enrollment = enrollmentsByCourseId.get(course.id);
                    if (!enrollment) return false;

                    const numericProgress = Number.isFinite(enrollment.progress) ? (enrollment.progress as number) : 0;

                    return enrollment.status === UserEnrollmentStatus.Completed || numericProgress >= 100;
                });
            },
            target: $finishedChallenges
        });

        sample({
            clock: challengeCardClicked,
            fn: (s) => {
                if (s.tags?.includes("challenge")) {
                    return `/course/${s.id}`;
                } else {
                    return `/challenge/${s.id}`;
                }
            },
            target: this.routerModule.events.routerPush
        });
    }
}
